// Form fields, status chips and the field error for the pages that ask for input: the web app's
// side of mockups/kit/surface.css section 19 (.wp-label, .wp-field, .wp-select, .wp-textarea,
// .wp-err, .wp-chip), with the same sizes, borders and states, written as the theme's utilities.
import { cn, Icon } from "@waypoint/surface";
import type { ComponentProps, ReactNode } from "react";

const control = [
  "w-full min-h-8.5 rounded-8 border border-wp-line-strong bg-wp-bg/58 px-2.5 py-1.5 font-sans text-13 text-wp-text",
  "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-wp-accent",
  "aria-invalid:border-stop-soft/62",
];

export function Label({ className, ...props }: ComponentProps<"label">) {
  return (
    <label
      className={cn("mb-1 block text-12 font-semibold text-wp-text-2", className)}
      {...props}
    />
  );
}

export function Select({ className, ...props }: ComponentProps<"select">) {
  return (
    <select
      className={cn(
        control,
        "appearance-none pr-7.5 bg-[linear-gradient(45deg,transparent_50%,var(--wp-text-3)_50%),linear-gradient(135deg,var(--wp-text-3)_50%,transparent_50%)]",
        "bg-[length:5px_5px] bg-[position:calc(100%-15px)_50%,calc(100%-10px)_50%] bg-no-repeat",
        className,
      )}
      {...props}
    />
  );
}

export function TextArea({ className, ...props }: ComponentProps<"textarea">) {
  return (
    <textarea className={cn(control, "min-h-21 resize-y leading-[1.45]", className)} {...props} />
  );
}

/** A field's error, announced when it appears; tie it to its field with aria-describedby. */
export function FieldError({ id, children }: { id: string; children?: ReactNode }) {
  return (
    <div id={id} role="alert" className="mt-1 flex items-start gap-1.5 text-12 text-stop-soft">
      {children === undefined ? null : (
        <>
          <Icon name="alert" size={14} />
          <span>{children}</span>
        </>
      )}
    </div>
  );
}

export type ChipTone = "plain" | "open" | "blocks" | "stop" | "done" | "quiet";

const TONES: Record<ChipTone, string> = {
  plain: "border-wp-line-strong text-wp-text-2",
  open: "border-caution/48 bg-caution/12 text-caution",
  blocks: "border-transparent bg-caution text-wp-accent-ink",
  stop: "border-stop/48 bg-stop/14 text-stop-soft",
  done: "border-wp-accent/44 bg-wp-accent/13 text-wp-accent",
  quiet: "border-dashed border-wp-line-strong text-wp-text-3",
};

/** A state in a word and, where it helps, a glyph: never colour alone. */
export function StatusChip({
  tone = "plain",
  className,
  ...props
}: ComponentProps<"span"> & { tone?: ChipTone }) {
  return (
    <span
      data-tone={tone}
      className={cn(
        "inline-flex h-5 items-center gap-1.25 rounded-pill border px-2 text-11 leading-none font-semibold whitespace-nowrap",
        TONES[tone],
        className,
      )}
      {...props}
    />
  );
}
